import type { Customer } from "./types";

/**
 * Is the customer's name only what the agent *heard* on a call?
 *
 * Mirror of `backend/src/services/customers/services/name-verification.ts` — read it for
 * the why. In short: the name stands on the family name (else the given name, else the
 * display name); that field is unsure when its value matches candidates recorded from
 * calls and none of them was spelled, written by the customer or proven by the line. A
 * value no candidate explains was set by a person or a sync, and is trusted.
 *
 * Lists that only carry a `customer_id` (calendar events, reminders, Messagerie) get the
 * same answer from the server as `unsure_customer_name`.
 */
type NameCarrier = Partial<
  Pick<
    Customer,
    "given_name" | "family_name" | "display_name" | "unverified_infos"
  >
> | null;

const clean = (value?: string | null) => (value || "").trim();

/**
 * The customer's name as every screen writes it: given and family name, else the
 * display name (an imported contact may carry nothing else), else `""` — the caller
 * picks its own "unknown". `prefix` adds the civility ("Mme"), for the record's own page.
 */
export const customerDisplayName = (
  customer?:
    | (NonNullable<NameCarrier> & Partial<Pick<Customer, "prefix">>)
    | null,
  { prefix = false }: { prefix?: boolean } = {},
): string => {
  const name = [customer?.given_name, customer?.family_name]
    .map(clean)
    .filter(Boolean)
    .join(" ");
  if (!name) return clean(customer?.display_name);
  return prefix && clean(customer?.prefix)
    ? `${clean(customer?.prefix)} ${name}`
    : name;
};

/** Mirror of `backend/src/services/customers/services/sources.ts`. */
const SOURCE_CONFIDENCE_ORDER = [
  "user_voice",
  "user_spelled",
  "user_written",
  "transport_layer",
];

const isConfirmingSource = (source: string) =>
  SOURCE_CONFIDENCE_ORDER.indexOf(source) >=
  SOURCE_CONFIDENCE_ORDER.indexOf("user_spelled");

/** At least spelled — or no candidate explains the value (a person or a sync set it). */
const fieldConfirmed = (
  customer: NameCarrier | undefined,
  field: "given_name" | "family_name" | "display_name",
) => {
  const value = clean(customer?.[field]);
  if (!value) return false;
  const explaining = (customer?.unverified_infos?.[field] || []).filter(
    (item) => clean(item.value) === value,
  );
  return (
    !explaining.length ||
    explaining.some((item) => isConfirmingSource(item.source))
  );
};

export const isNameUnsure = (customer?: NameCarrier): boolean => {
  const field = (["family_name", "given_name", "display_name"] as const).find(
    (candidate) => clean(customer?.[candidate]),
  );
  return !!field && !fieldConfirmed(customer, field);
};
